import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { FormHeader } from "../FormHeader";
import { FormFooter } from "../FormFooter";
import { Icon } from "../../lib";
import { IconButton } from "../IconButton";
import { Text } from "../Text";
import { Button } from "../Button";

const uploadBoxVariants = cva(
  "relative flex flex-col items-center justify-center border-2 border-dashed rounded-large transition-all font-display",
  {
    variants: {
      size: {
        small: "min-h-[120px] p-4 gap-2",
        medium: "min-h-[160px] p-6 gap-3",
        large: "min-h-[200px] p-8 gap-4",
      },
      validationState: {
        none: `
          border-action-outline-neutral-faded 
          hover:border-action-outline-primary-hover 
          focus-within:border-action-outline-primary-hover 
          focus-within:ring-2 
          ring-action-outline-primary-faded-hover
          bg-surface-fill-neutral-intense`,
        positive: `
          border-action-outline-positive-default 
          focus-within:border-action-outline-positive-hover
          focus-within:ring-2
          ring-action-outline-positive-faded-hover
          bg-surface-fill-neutral-intense`,
        negative: `
          border-action-outline-negative-default 
          focus-within:border-action-outline-negative-hover
          focus-within:ring-2 
          ring-action-outline-negative-faded-hover
          bg-surface-fill-neutral-intense`,
      },
      isDisabled: {
        true: `
          border-[var(--border-width-thinner)]
          hover:border-action-outline-neutral-disabled
          border-action-outline-neutral-disabled
          bg-surface-fill-neutral-intense 
          cursor-not-allowed 
          opacity-60`,
        false: "cursor-pointer",
      },
      isDragging: {
        true: "border-action-outline-primary-hover bg-action-fill-primary-faded",
        false: "",
      },
    },
    defaultVariants: {
      size: "medium",
      validationState: "none",
      isDisabled: false,
      isDragging: false,
    },
  }
);

export interface UploadedFile {
  file?: File;
  url?: string;
  name?: string;
  size?: number;
  preview?: string;
  id: string;
}

export interface UploadBoxProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  label?: string;
  helperText?: string;
  errorText?: string;
  successText?: string;
  size?: "small" | "medium" | "large";
  validationState?: "none" | "positive" | "negative";
  isDisabled?: boolean;
  isRequired?: boolean;
  isOptional?: boolean;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // in bytes
  maxFiles?: number;
  value?: File | File[] | UploadedFile | UploadedFile[] | string | string[];
  onChange?: (files: File | File[] | null) => void;
  onFileRemove?: (file: File | UploadedFile) => void;
  containerClassName?: string;
  labelClassName?: string;
  uploadAreaClassName?: string;
  previewClassName?: string;
  infoHeading?: string;
  infoDescription?: string;
  LinkComponent?: React.ReactNode;
  linkText?: string;
  linkHref?: string;
  onLinkClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  uploadText?: string;
  dragText?: string;
  showPreview?: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const isImageFile = (file?: File, url?: string): boolean => {
  if (file) {
    return file.type.startsWith("image/");
  }
  if (url) {
    const lowerUrl = url.toLowerCase();
    return (
      lowerUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i) !== null ||
      url.match(/data:image\//) !== null
    );
  }
  return false;
};

const isPdfFile = (file?: File, url?: string): boolean => {
  if (file) {
    return file.type === "application/pdf";
  }
  if (url) {
    return url.toLowerCase().match(/\.pdf(\?.*)?$/i) !== null;
  }
  return false;
};

const isPreviewable = (file?: File, url?: string): boolean => {
  return isImageFile(file, url) || isPdfFile(file, url);
};

const getFileNameFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const fileName = pathname.split("/").pop() || "file";
    return decodeURIComponent(fileName);
  } catch {
    // If URL parsing fails, try to extract from the string
    const parts = url.split("/");
    const lastPart = parts[parts.length - 1] || "file";
    return decodeURIComponent(lastPart.split("?")[0]);
  }
};

// Component to handle image preview with error fallback
const ImagePreview = ({ src, alt }: { src: string; alt: string }) => {
  const [hasError, setHasError] = React.useState(false);

  if (hasError) {
    return (
      <div className="shrink-0 w-16 h-16 rounded-medium  flex items-center justify-center bg-surface-fill-neutral-subtle">
        <Icon
          name="file"
          size={24}
          className="text-surface-ink-neutral-subtle"
        />
      </div>
    );
  }

  return (
    <div className="shrink-0 w-16 h-16 rounded-medium overflow-hidden ">
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        onError={() => setHasError(true)}
      />
    </div>
  );
};

const UploadBox = React.forwardRef<HTMLDivElement, UploadBoxProps>(
  (
    {
      label,
      helperText,
      errorText,
      successText,
      size = "medium",
      validationState = "none",
      isDisabled = false,
      isRequired = false,
      isOptional = false,
      accept,
      multiple = false,
      maxSize,
      maxFiles,
      value,
      onChange,
      onFileRemove,
      containerClassName,
      labelClassName,
      uploadAreaClassName,
      previewClassName,
      infoHeading,
      infoDescription,
      LinkComponent,
      linkText,
      linkHref,
      onLinkClick,
      uploadText,
      dragText,
      showPreview = true,
      className,
      ...props
    },
    ref
  ) => {
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const [uploadedFiles, setUploadedFiles] = React.useState<UploadedFile[]>(
      []
    );
    const [error, setError] = React.useState<string | null>(null);

    // Initialize uploaded files from value prop
    React.useEffect(() => {
      if (value) {
        const filesArray = Array.isArray(value) ? value : [value];
        const processedFiles = filesArray.map((item) => {
          if (item instanceof File) {
            const uploadedFile: UploadedFile = {
              file: item,
              name: item.name,
              size: item.size,
              id: `${item.name}-${item.size}-${item.lastModified}`,
            };
            if (isImageFile(item) && showPreview) {
              uploadedFile.preview = URL.createObjectURL(item);
            }
            return uploadedFile;
          } else if (typeof item === "string") {
            // It's a URL string
            const uploadedFile: UploadedFile = {
              url: item,
              name: getFileNameFromUrl(item),
              id: `url-${item}-${Date.now()}`,
            };
            // Always set preview for URLs when showPreview is true
            // The rendering logic will determine if it's an image or PDF
            if (showPreview) {
              uploadedFile.preview = item;
            }
            return uploadedFile;
          } else {
            // It's already an UploadedFile
            const uploadedFile = item as UploadedFile;
            // If it has a URL but no preview set, set it
            if (uploadedFile.url && !uploadedFile.preview && showPreview) {
              uploadedFile.preview = uploadedFile.url;
            }
            return uploadedFile;
          }
        });
        setUploadedFiles(processedFiles);
      } else {
        setUploadedFiles([]);
      }
    }, [value, showPreview]);

    // Cleanup preview URLs (only revoke object URLs, not regular URLs)
    React.useEffect(() => {
      return () => {
        uploadedFiles.forEach((uploadedFile) => {
          // Only revoke object URLs (created with URL.createObjectURL)
          // Regular URLs (http/https) should not be revoked
          if (
            uploadedFile.preview &&
            uploadedFile.preview.startsWith("blob:")
          ) {
            URL.revokeObjectURL(uploadedFile.preview);
          }
        });
      };
    }, [uploadedFiles]);

    const processFiles = (files: FileList | File[]): File[] => {
      const fileArray = Array.from(files);
      const validFiles: File[] = [];
      let currentError: string | null = null;

      // Check max files limit
      if (maxFiles && uploadedFiles.length + fileArray.length > maxFiles) {
        currentError = `Maximum ${maxFiles} file(s) allowed`;
        setError(currentError);
        return validFiles;
      }

      fileArray.forEach((file) => {
        // Check file size
        if (maxSize && file.size > maxSize) {
          currentError = `File "${
            file.name
          }" exceeds maximum size of ${formatFileSize(maxSize)}`;
          setError(currentError);
          return;
        }

        // Check if file type is accepted
        if (accept) {
          const acceptedTypes = accept.split(",").map((type) => type.trim());
          const isAccepted = acceptedTypes.some((type) => {
            if (type.startsWith(".")) {
              return file.name.toLowerCase().endsWith(type.toLowerCase());
            }
            if (type.includes("/*")) {
              const baseType = type.split("/")[0];
              return file.type.startsWith(baseType + "/");
            }
            return file.type === type;
          });

          if (!isAccepted) {
            currentError = `File type "${file.type}" is not accepted`;
            setError(currentError);
            return;
          }
        }

        validFiles.push(file);
      });

      if (currentError) {
        setError(currentError);
      } else {
        setError(null);
      }

      return validFiles;
    };

    const handleFiles = (files: File[]) => {
      if (files.length === 0) return;

      const newUploadedFiles: UploadedFile[] = files.map((file) => {
        const uploadedFile: UploadedFile = {
          file,
          id: `${file.name}-${file.size}-${file.lastModified}-${Date.now()}`,
        };

        if (isImageFile(file) && showPreview) {
          uploadedFile.preview = URL.createObjectURL(file);
        }

        return uploadedFile;
      });

      const updatedFiles = multiple
        ? [...uploadedFiles, ...newUploadedFiles]
        : newUploadedFiles;

      setUploadedFiles(updatedFiles);

      // Call onChange with File objects only (filter out URL-only items)
      if (onChange) {
        const filesToReturn = updatedFiles
          .map((uf) => uf.file)
          .filter((file): file is File => file !== undefined);
        onChange(multiple ? filesToReturn : filesToReturn[0] || null);
      }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        const validFiles = processFiles(e.target.files);
        if (validFiles.length > 0) {
          handleFiles(validFiles);
        }
        // Reset input value to allow selecting the same file again
        e.target.value = "";
      }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDisabled) {
        setIsDragging(true);
      }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (isDisabled) return;

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const validFiles = processFiles(e.dataTransfer.files);
        if (validFiles.length > 0) {
          handleFiles(validFiles);
        }
      }
    };

    const handleClick = () => {
      if (!isDisabled && fileInputRef.current) {
        fileInputRef.current.click();
      }
    };

    const handleRemoveFile = (
      e: React.MouseEvent,
      uploadedFile: UploadedFile
    ) => {
      e.stopPropagation();
      if (isDisabled) return;

      // Revoke preview URL if exists
      if (uploadedFile.preview) {
        URL.revokeObjectURL(uploadedFile.preview);
      }

      const updatedFiles = uploadedFiles.filter(
        (f) => f.id !== uploadedFile.id
      );
      setUploadedFiles(updatedFiles);

      if (onFileRemove) {
        onFileRemove(uploadedFile);
      }

      if (onChange) {
        // Only return File objects (filter out URL-only items)
        const filesToReturn = updatedFiles
          .map((uf) => uf.file)
          .filter((file): file is File => file !== undefined);
        onChange(multiple ? filesToReturn : filesToReturn[0] || null);
      }
    };

    // Determine which helper text to show
    const displayHelperText = errorText || successText || helperText || error;
    const currentValidationState = errorText
      ? "negative"
      : successText
      ? "positive"
      : validationState;

    const sizeConfig = {
      small: {
        gap: "gap-2",
        iconSize: 16,
        textSize: "text-body-small-medium",
      },
      medium: {
        gap: "gap-2",
        iconSize: 16,
        textSize: "text-body-small-medium",
      },
      large: {
        gap: "gap-3",
        iconSize: 16,
        textSize: "text-body-small-medium",
      },
    };

    const config = sizeConfig[size];

    const defaultUploadText = "Click to upload or drag and drop";
    const defaultDragText = "Drop files here";

    return (
      <div
        ref={ref}
        className={cn("w-full flex flex-col", config.gap, containerClassName)}
        {...props}
      >
        {label && (
          <FormHeader
            label={label}
            size={size}
            isRequired={isRequired}
            isOptional={isOptional}
            infoHeading={infoHeading}
            infoDescription={infoDescription}
            LinkComponent={LinkComponent}
            linkText={linkText}
            linkHref={linkHref}
            onLinkClick={onLinkClick}
            className="mb-2"
            labelClassName={labelClassName}
          />
        )}

        <div
          className={cn(
            uploadBoxVariants({
              size,
              validationState: currentValidationState,
              isDisabled,
              isDragging,
            }),
            uploadAreaClassName,
            className
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={isDisabled}
            onChange={handleFileInputChange}
            className="hidden"
            aria-label={label || "File upload"}
          />

          {uploadedFiles.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 text-center">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-surface-fill-neutral-subtle">
                <Icon name="upload" size={config.iconSize} />
              </div>
              <div className="flex flex-col gap-1">
                <Text
                  variant="body"
                  size="small"
                  weight="medium"
                  color={isDisabled ? "disabled" : "default"}
                >
                  {isDragging
                    ? dragText || defaultDragText
                    : uploadText || defaultUploadText}
                </Text>
                {accept && (
                  <Text
                    variant="caption"
                    size="small"
                    color={isDisabled ? "disabled" : "muted"}
                  >
                    Accepted: {accept}
                  </Text>
                )}
                {maxSize && (
                  <Text
                    variant="caption"
                    size="small"
                    color={isDisabled ? "disabled" : "muted"}
                  >
                    Max size: {formatFileSize(maxSize)}
                  </Text>
                )}
              </div>
            </div>
          ) : (
            <div className={cn("w-full flex flex-col gap-3", previewClassName)}>
              {uploadedFiles.map((uploadedFile) => {
                const { file, url, preview, name, size, id } = uploadedFile;
                const fileName =
                  name ||
                  file?.name ||
                  (url ? getFileNameFromUrl(url) : "Unknown file");
                const fileSize = size || file?.size;
                const canPreview = showPreview && preview;
                const isImage = isImageFile(file, url);
                const isPdf = isPdfFile(file, url);
                // For URLs, try to show as image if not explicitly a PDF
                // If it's a URL without extension, assume it might be an image
                // Always try to show URLs as images unless they're explicitly PDFs
                const shouldShowAsImage =
                  canPreview && preview && (isImage || (url && !isPdf));

                return (
                  <div
                    key={id}
                    className="flex items-start gap-3 p-3 border border-surface-outline-neutral-muted rounded-large bg-surface-fill-neutral-intense"
                  >
                    {shouldShowAsImage ? (
                      <ImagePreview src={preview} alt={fileName} />
                    ) : canPreview && isPdf ? (
                      <div className="shrink-0 w-16 h-16 rounded-medium border border-action-outline-neutral-faded flex items-center justify-center bg-surface-fill-neutral-subtle">
                        <Icon
                          name="file"
                          size={24}
                          className="text-surface-ink-neutral-subtle"
                        />
                      </div>
                    ) : (
                      <div className="shrink-0 w-16 h-16 rounded-medium border border-action-outline-neutral-faded flex items-center justify-center bg-surface-fill-neutral-subtle">
                        <Icon
                          name="file"
                          size={24}
                          className="text-surface-ink-neutral-subtle"
                        />
                      </div>
                    )}

                    <div className="flex-1 min-w-0 flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Text
                          variant="body"
                          size="small"
                          weight="medium"
                          color={isDisabled ? "disabled" : "default"}
                          as="span"
                          className="truncate"
                          title={fileName}
                        >
                          {fileName}
                        </Text>
                      </div>
                      {fileSize !== undefined && (
                        <Text
                          variant="caption"
                          size="small"
                          color={isDisabled ? "disabled" : "muted"}
                          as="span"
                        >
                          {formatFileSize(fileSize)}
                        </Text>
                      )}
                    </div>

                    {!isDisabled && (
                      <IconButton
                        icon="close"
                        size="xsmall"
                        onClick={(e) => handleRemoveFile(e, uploadedFile)}
                        aria-label={`Remove ${fileName}`}
                      />
                    )}
                  </div>
                );
              })}

              {multiple && !isDisabled && (
                <Button
                  variant="tertiary"
                  color="primary"
                  size="small"
                  onClick={handleClick}
                  leadingIcon={<Icon name="add" size={16} />}
                  className="mt-2 w-fit"
                >
                  Add more files
                </Button>
              )}
            </div>
          )}
        </div>

        <FormFooter
          helperText={displayHelperText || undefined}
          validationState={
            currentValidationState === "none"
              ? "default"
              : currentValidationState
          }
          size={size}
          isDisabled={isDisabled}
          className="mt-1"
        />
      </div>
    );
  }
);

UploadBox.displayName = "UploadBox";

export { UploadBox, uploadBoxVariants };
